/**
 * Zod Schemas for Showcase Search API
 * /pages/api/search/showcase.ts
 */

import { z } from 'zod';

// Re-use common schemas from the main search schema
import { SearchListingSchema, ListingPhotoSchema, ListingUserSchema } from '../../search.schema';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const ShowcaseRequestQuerySchema = z.object({
  // Required
  categoryGroupId: z.string(),
  // Showcase-specific
  minProductsPerCategory: z.string().optional().default('7'),
  categories: z.string().optional(), // Specific category IDs within the group
  // Text search
  q: z.string().optional(),
  // Price filters
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  // Status filters
  sold: z.string().optional(), // Include sold items (sold=1)
  status: z.string().optional(), // Explicit status filter (comma-separated)
  // Attribute filters
  itemCondition: z.string().optional(),
  hasPhoto: z.string().optional(),
  shippable: z.string().optional(),
  // Membership filters
  membershipType: z.string().optional(),
  featured: z.string().optional(),
  // User filters
  userId: z.string().optional(),
  excludeUserId: z.string().optional(),
  // Category exclusions
  excludeCategoryId: z.string().optional(),
  // Data source
  dataSourceId: z.string().optional(),
  // Geo filters
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  distance: z.string().optional(),
  // Sorting
  sortBy: z.string().optional(),
});

export type ShowcaseRequestQuery = z.infer<typeof ShowcaseRequestQuerySchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

// Showcase group item
export const ShowcaseGroupSchema = z.object({
  categoryId: z.number(),
  categoryName: z.string(),
  categorySlug: z.string(),
  listings: z.array(SearchListingSchema),
});

export type ShowcaseGroup = z.infer<typeof ShowcaseGroupSchema>;

// Success response
export const ShowcaseSuccessResponseSchema = z.object({
  success: z.literal(true),
  fullShowcases: z.array(ShowcaseGroupSchema),
  partialShowcases: z.array(ShowcaseGroupSchema),
  hasEnoughForShowcase: z.boolean(),
  total: z.number(),
});

export type ShowcaseSuccessResponse = z.infer<typeof ShowcaseSuccessResponseSchema>;

// Error response
export const ShowcaseErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type ShowcaseErrorResponse = z.infer<typeof ShowcaseErrorResponseSchema>;

// Combined response using discriminated union
export const ShowcaseResponseSchema = z.discriminatedUnion('success', [
  ShowcaseSuccessResponseSchema,
  ShowcaseErrorResponseSchema,
]);

export type ShowcaseResponse = z.infer<typeof ShowcaseResponseSchema>;
